// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * "Sky above me" (docs/AYNI_XR_SATELLITES_PLAN.md, the AR flagship's second
 * mode): the satellites over the viewer's head right now, drawn on the real
 * sky through the passthrough, where they are, with a horizon, two
 * elevation rings and the four compass points.
 *
 * Three steps, each said in the status line:
 * 1. where: the device's location if the viewer allows it, otherwise a tap
 *    on their place on the globe. It stays in this session, never saved or
 *    sent anywhere;
 * 2. north: a phone's compass sets it by itself; on a headset, or a phone
 *    without one, the viewer faces north and presses (the phone: taps);
 * 3. the sky: a dome 25 m out, centred on the viewer's head and following
 *    it (not its turning), with the satellites over the horizon, their
 *    trails, the stations' names and the selected one's pass.
 *
 * The drawing is vrSatellites' own, given a projection onto the dome.
 */
import type * as THREE from 'three'
import { t } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import type { SatelliteLayer, SkyPass } from './satellites/satelliteLayer'
import { satelliteDatasets } from './satellites/satelliteText'
import {
  cameraHeading,
  clarkeBelt,
  compassPoint,
  inEarthShadow,
  lookAngles,
  nextPass,
  observerFrame,
  sunDirectionEcf,
  xrYaw,
  type Observer,
  type ObserverFrame,
} from './satellites/skyMath'
import { createVrSatellites, shortName, type VrSatellitesHandle } from './vrSatellites'
import { createSkyLines, createSkyPointer, createSkyPrompt } from './vrSkyGuides'

/** The dome's radius, metres: beyond the walls, so the passthrough room never hides it. */
export const SKY_DOME_M = 25
/** Objects a little under the horizon are dropped. */
const HORIZON_SIN = Math.sin((-0.5 * Math.PI) / 180)
const ISS_NORAD = 25544
/** How long the "ready" summary stays in front of the viewer, ms. */
const SUMMARY_MS = 14_000
/** The compass points' locale keys, N first, clockwise (two-letter ones are made of these). */
const POINT_KEYS = ['satellites.sky.n', 'satellites.sky.e', 'satellites.sky.s', 'satellites.sky.w'] as const

export type SkyStage = 'locate' | 'north' | 'ready'

export interface VrSatelliteSkyOptions {
  handheld: boolean
  /** The device's place, or null if the viewer says no (or there is none). */
  geolocate?: () => Promise<Observer | null>
  /** AYNI (owner, 2026-11-04): true while the sky stands down (the viewer is inside a globe grown past 2 m). */
  hidden?: () => boolean
}

export interface VrSatelliteSkyHandle {
  readonly group: THREE.Group
  readonly stage: SkyStage | null
  /** The prompt in front of the viewer and the pointer (for checks). */
  readonly guides: { prompt: THREE.Mesh; pointer: THREE.Group; lines: THREE.Group }
  /** Per XR frame, while the layer's sky is on. */
  update(camera: THREE.Camera, viewportHeightPx: number): void
  /** A quick press: sets north while it is asked for, else picks a satellite. True when taken. */
  onRayTap(origin: THREE.Vector3, direction: THREE.Vector3, camera: THREE.Camera): boolean
  /** A tap on the globe while the viewer's place is asked for. True when taken. */
  onGlobePlace(latDeg: number, lonDeg: number): boolean
  /** Ask for north again. */
  resetNorth(): void
  dispose(): void
}

export function createVrSatelliteSky(
  THREE_: typeof THREE,
  scene: THREE.Object3D,
  layer: SatelliteLayer,
  opts: VrSatelliteSkyOptions,
): VrSatelliteSkyHandle {
  const root = new THREE_.Group()
  root.name = 'satellite-sky'
  scene.add(root)
  root.visible = false

  let observer: Observer | null = null
  let frame: ObserverFrame | null = null
  let northYaw: number | null = null
  let started = false
  let locating = false
  let compass: { alpha: number; beta: number; gamma: number; at: number } | null = null
  let lastCounts = 0
  let passText = ''
  let passAt = 0
  /** The NORAD number the pass and the pointer were worked out for. */
  let passFor = -1
  let pass: { riseMs: number; maxEl: number; riseAz: number } | null = null
  /** The catalog's satellites' next passes (step 3 of the catalog plan): the line, and when they were worked out. */
  let catalogText = ''
  let catalogAt = 0
  let readyAt = 0
  let lastStage: SkyStage | null = null

  const stage = (): SkyStage => (!observer ? 'locate' : northYaw === null ? 'north' : 'ready')

  // --- the dome's guides: horizon, 30° and 60° rings, N E S W ----------------
  const guides = new THREE_.Group()
  root.add(guides)
  const ringMat = new THREE_.LineBasicMaterial({ color: 0x9fe8ff, transparent: true, opacity: 0.35, depthWrite: false, depthTest: false })
  const faintMat = new THREE_.LineBasicMaterial({ color: 0x9fe8ff, transparent: true, opacity: 0.14, depthWrite: false, depthTest: false })
  const ring = (elDeg: number, mat: THREE.LineBasicMaterial) => {
    const el = (elDeg * Math.PI) / 180
    const r = SKY_DOME_M * Math.cos(el)
    const y = SKY_DOME_M * Math.sin(el)
    const pts: number[] = []
    for (let a = 0; a < 180; a++) {
      const ang = (a / 180) * Math.PI * 2
      pts.push(r * Math.sin(ang), y, -r * Math.cos(ang))
    }
    const g = new THREE_.BufferGeometry()
    g.setAttribute('position', new THREE_.BufferAttribute(new Float32Array(pts), 3))
    const line = new THREE_.LineLoop(g, mat)
    line.frustumCulled = false
    line.renderOrder = 5
    guides.add(line)
  }
  ring(0, ringMat)
  ring(30, faintMat)
  ring(60, faintMat)
  const letters: THREE.Sprite[] = []
  const letterKeys = ['satellites.sky.n', 'satellites.sky.e', 'satellites.sky.s', 'satellites.sky.w'] as const
  letterKeys.forEach((key, i) => {
    const s = textSprite(THREE_, t(key), i === 0 ? '#ff6b6b' : '#e6faff')
    const az = (i * Math.PI) / 2
    const el = (4 * Math.PI) / 180
    s.position.set(SKY_DOME_M * Math.cos(el) * Math.sin(az), SKY_DOME_M * Math.sin(el), -SKY_DOME_M * Math.cos(el) * Math.cos(az))
    s.scale.set(1.6, 1.6, 1)
    guides.add(s)
    letters.push(s)
  })

  // --- the satellites, projected onto the dome --------------------------------
  const project = (x: number, y: number, z: number, out: Float32Array, o: number): void => {
    if (!frame || northYaw === null || x !== x) {
      out[o] = out[o + 1] = out[o + 2] = NaN
      return
    }
    const rx = x - frame.o[0]
    const ry = y - frame.o[1]
    const rz = z - frame.o[2]
    const range = Math.hypot(rx, ry, rz)
    const u = (rx * frame.u[0] + ry * frame.u[1] + rz * frame.u[2]) / range
    if (u < HORIZON_SIN) {
      out[o] = out[o + 1] = out[o + 2] = NaN
      return
    }
    const e = rx * frame.e[0] + ry * frame.e[1] + rz * frame.e[2]
    const n = rx * frame.n[0] + ry * frame.n[1] + rz * frame.n[2]
    const yaw = northYaw + Math.atan2(e, n)
    const ce = Math.sqrt(Math.max(0, 1 - u * u))
    out[o] = SKY_DOME_M * ce * Math.sin(yaw)
    out[o + 1] = SKY_DOME_M * u
    out[o + 2] = -SKY_DOME_M * ce * Math.cos(yaw)
  }
  const sats: VrSatellitesHandle = createVrSatellites(THREE_, root, 1, layer, {
    project,
    projectKey: () => `${observer?.latDeg},${observer?.lonDeg},${northYaw}`,
    // About half a degree across at 25 m, and never under 4 px: easy to find on the real sky.
    pointSize: 0.24,
    minPx: 4,
    geoRing: false,
    path: 'track',
    occluder: null,
    visible: () => stage() === 'ready',
  })

  // --- the guides: a prompt and a pointer in front of the viewer, lines on the dome ---
  const prompt = createSkyPrompt(THREE_)
  const pointer = createSkyPointer(THREE_)
  const lines = createSkyLines(THREE_)
  scene.add(prompt.mesh)
  scene.add(pointer.group)
  root.add(lines.group)
  const belt = clarkeBelt(1)
  let beltKey = ''
  const scratchP = new Float32Array(3)
  const targetWorld = new THREE_.Vector3()

  /** Where an Earth-fixed point stands on the dome, or null under the horizon. */
  function domeOf(x: number, y: number, z: number): [number, number, number] | null {
    project(x, y, z, scratchP, 0)
    return scratchP[0] === scratchP[0] ? [scratchP[0], scratchP[1], scratchP[2]] : null
  }

  /** "N", "NE", … in the page's language. */
  function pointName(az: number): string {
    const k = compassPoint(az)
    const main = t(POINT_KEYS[Math.floor(k / 2) % 4])
    if (k % 2 === 0) return main
    // NE, SE, SW, NW: north or south first.
    const ns = t(POINT_KEYS[k === 1 || k === 7 ? 0 : 2])
    const ew = t(POINT_KEYS[k === 1 || k === 3 ? 1 : 3])
    return ns + ew
  }

  function updateGuides(camera: THREE.Camera): void {
    const engine = layer.engine
    const p = engine.current
    // Drop lines to the named objects and the selected one.
    const drops: Array<[number, number, number]> = []
    for (let i = 0; i < engine.count; i++) {
      if (!engine.mask[i] || !(engine.infos[i].station || i === layer.selected)) continue
      const d = domeOf(p[i * 3], p[i * 3 + 1], p[i * 3 + 2])
      if (d) drops.push(d)
    }
    lines.setDrops(drops)
    // The belt, again whenever the place or north changes.
    const key = `${observer?.latDeg},${observer?.lonDeg},${northYaw}`
    if (key !== beltKey) {
      beltKey = key
      const out = new Float32Array(belt.length)
      for (let o = 0; o < belt.length; o += 3) project(belt[o], belt[o + 1], belt[o + 2], out, o)
      lines.setBelt(out)
    }
    // The pointer: toward the selected object, or where it will rise.
    const sel = layer.selected
    const info = sel !== null ? engine.infos[sel] : null
    if (!info || !frame || northYaw === null) {
      pointer.update(camera, null, '')
      return
    }
    const name = shortName(info.name)
    const d = domeOf(p[sel! * 3], p[sel! * 3 + 1], p[sel! * 3 + 2])
    if (d) {
      const la = lookAngles(frame, p[sel! * 3], p[sel! * 3 + 1], p[sel! * 3 + 2])
      targetWorld.set(d[0], d[1], d[2]).applyQuaternion(root.quaternion).add(root.position)
      pointer.update(camera, targetWorld, t('satellites.sky.pointUp', {
        name,
        el: formatNumber(Math.round((la.el * 180) / Math.PI)),
        dir: pointName(la.az),
      }))
    } else if (pass && passFor === info.norad) {
      const yaw = northYaw + pass.riseAz
      targetWorld.set(SKY_DOME_M * Math.sin(yaw), 0, -SKY_DOME_M * Math.cos(yaw)).applyQuaternion(root.quaternion).add(root.position)
      pointer.update(camera, targetWorld, t('satellites.sky.pointRises', {
        name,
        time: formatDate(pass.riseMs, { hour: '2-digit', minute: '2-digit' }),
        dir: pointName(pass.riseAz),
      }))
    } else {
      pointer.update(camera, null, '')
    }
  }

  /** What the panel in front of the viewer says, if anything. */
  function promptText(st: SkyStage): string | null {
    if (st === 'locate') return locating ? t('satellites.sky.locating') : t('satellites.sky.where')
    if (st === 'north') return opts.handheld ? t('satellites.sky.northPhone') : t('satellites.sky.north')
    if (performance.now() - readyAt > SUMMARY_MS) return null
    const head = t('satellites.sky.above', { count: formatNumber(lastCounts), lit: formatNumber(litCount) })
    return [head, passText, catalogText, t(opts.handheld ? 'satellites.sky.hintPhone' : 'satellites.sky.hint')].filter(Boolean).join('\n')
  }

  // --- where, and north -------------------------------------------------------
  const onOrientation = (ev: DeviceOrientationEvent) => {
    if (ev.alpha === null || ev.beta === null || ev.gamma === null) return
    compass = { alpha: ev.alpha, beta: ev.beta, gamma: ev.gamma, at: performance.now() }
  }
  if (opts.handheld && typeof window !== 'undefined') {
    window.addEventListener('deviceorientationabsolute', onOrientation as EventListener)
  }

  function setObserver(obs: Observer): void {
    observer = obs
    frame = observerFrame(obs)
    passAt = 0
  }

  function start(): void {
    started = true
    if (observer || locating || !opts.geolocate) return
    locating = true
    void opts.geolocate().then((obs) => {
      locating = false
      if (obs && !observer) setObserver(obs)
    })
  }

  const scratch = new THREE_.Vector3()

  function northFromCamera(camera: THREE.Camera, heading: number): void {
    camera.getWorldDirection(scratch)
    northYaw = xrYaw(scratch.x, scratch.z) - heading
  }

  function statusText(): string {
    const st = stage()
    if (st === 'locate') return locating ? t('satellites.sky.locating') : t('satellites.sky.where')
    if (st === 'north') return opts.handheld ? t('satellites.sky.northPhone') : t('satellites.sky.north')
    const head = t('satellites.sky.above', { count: formatNumber(lastCounts), lit: formatNumber(litCount) })
    return [head, passText, catalogText].filter(Boolean).join('\n')
  }

  let litCount = 0
  let countsAt = 0

  function counts(now: number): void {
    if (!frame || now - countsAt < 1000) return
    countsAt = now
    const engine = layer.engine
    const sun = sunDirectionEcf(engine.time)
    let above = 0
    let lit = 0
    const p = engine.current
    for (let i = 0; i < engine.count; i++) {
      if (!engine.mask[i]) continue
      const x = p[i * 3]
      if (x !== x) continue
      if (lookAngles(frame, x, p[i * 3 + 1], p[i * 3 + 2]).el <= 0) continue
      above++
      if (!inEarthShadow(x, p[i * 3 + 1], p[i * 3 + 2], sun)) lit++
    }
    lastCounts = above
    litCount = lit
  }

  function updatePass(now: number): void {
    const engine = layer.engine
    const index = layer.selected ?? engine.infos.findIndex((i) => i.norad === ISS_NORAD)
    const norad = index >= 0 ? engine.infos[index]?.norad ?? -1 : -1
    if (!frame || index < 0 || (norad === passFor && passAt && now - passAt < 10 * 60_000)) return
    passAt = now
    passFor = norad
    pass = null
    const name = shortName(engine.infos[index].name)
    const start = engine.time
    const stepMs = 60_000
    const f = frame
    void engine
      .orbitPath(index, start, start + 24 * 3600_000, 24 * 60 + 1)
      .then((path) => {
        if (f !== frame || passFor !== norad) return
        pass = nextPass(f, path, start, stepMs)
        passText = pass
          ? t('satellites.sky.nextPass', {
              name,
              time: formatDate(pass.riseMs, { weekday: 'short', hour: '2-digit', minute: '2-digit' }),
              el: formatNumber(Math.round((pass.maxEl * 180) / Math.PI)),
            })
          : t('satellites.sky.noPass', { name })
      })
      .catch(() => {})
  }

  /**
   * The catalog's satellites over the viewer (owner: "passes over you at
   * 18:42; it measures the ground-motion data", with Load): every tracked
   * satellite of `sources.json` that feeds a dataset, its next pass in the
   * coming day (or overhead now), the three soonest kept on the layer for
   * the cards. Worked out once the sky is ready, again every ten minutes.
   */
  function updateCatalogPasses(now: number): void {
    const f = frame
    if (!f || !layer.sourcesIndex || (catalogAt && now - catalogAt < 10 * 60_000)) return
    catalogAt = now
    const engine = layer.engine
    const candidates: Array<{ index: number; norad: number }> = []
    for (const p of Object.values(layer.sourcesIndex.platforms)) {
      if (!p?.tracked || !p.datasets?.length) continue
      const index = engine.infos.findIndex((i) => i.norad === p.norad)
      if (index >= 0) candidates.push({ index, norad: p.norad })
      if (candidates.length >= 24) break
    }
    if (!candidates.length) {
      catalogText = ''
      layer.setSkyPasses([])
      return
    }
    const start = engine.time
    const stepMs = 60_000
    void Promise.all(
      candidates.map(async ({ index, norad }): Promise<SkyPass | null> => {
        const info = engine.infos[index]
        if (!info) return null
        // Read the position before the await: the engine's buffers move to the worker between frames.
        const cur = engine.current
        const x = cur[index * 3]
        const elNow = x === x ? lookAngles(f, x, cur[index * 3 + 1], cur[index * 3 + 2]).el : -1
        const overhead = elNow > (10 * Math.PI) / 180
        const path = await engine.orbitPath(index, start, start + 24 * 3600_000, 24 * 60 + 1)
        const p = overhead ? null : nextPass(f, path, start, stepMs)
        if (!overhead && !p) return null
        const rows = satelliteDatasets(layer, norad)
        if (!rows.length) return null
        const name = shortName(info.name)
        const maxElDeg = Math.round(((overhead ? elNow : p!.maxEl) * 180) / Math.PI)
        const text = overhead
          ? t('satellites.sky.overheadNow', { name, el: formatNumber(maxElDeg), dataset: rows[0].title })
          : t('satellites.sky.catalogPass', { name, time: formatDate(p!.riseMs, { weekday: 'short', hour: '2-digit', minute: '2-digit' }), el: formatNumber(maxElDeg), dataset: rows[0].title })
        return { norad, name, riseMs: overhead ? start : p!.riseMs, maxElDeg, overhead, datasets: rows.map((r) => r.id), text }
      }),
    )
      .then((found) => {
        if (f !== frame) return
        const passes = found.filter((p): p is SkyPass => !!p).sort((a, b) => Number(b.overhead) - Number(a.overhead) || a.riseMs - b.riseMs).slice(0, 3)
        catalogText = passes[0]?.text ?? ''
        layer.setSkyPasses(passes)
      })
      .catch(() => {})
  }

  return {
    group: root,
    guides: { prompt: prompt.mesh, pointer: pointer.group, lines: lines.group },
    get stage() {
      return layer.skyOn ? stage() : null
    },

    update(camera, viewportHeightPx) {
      root.visible = layer.skyOn && !(opts.hidden?.() ?? false)
      if (!layer.skyOn) {
        started = false
        lastStage = null
        prompt.update(camera, null)
        pointer.update(camera, null, '')
        return
      }
      if (!started) start()
      camera.getWorldPosition(root.position)
      // A phone's compass sets north by itself, once a fresh reading is in.
      if (stage() === 'north' && compass && performance.now() - compass.at < 1000) {
        northFromCamera(camera, cameraHeading(compass.alpha, compass.beta, compass.gamma))
      }
      guides.visible = northYaw !== null
      // The guides are drawn with north at −z: turn them to the real north.
      guides.rotation.y = -(northYaw ?? 0)
      const now = performance.now()
      counts(now)
      updatePass(now)
      if (stage() === 'ready') updateCatalogPasses(now)
      sats.update(camera, viewportHeightPx)
      const st = stage()
      if (st !== lastStage) {
        lastStage = st
        if (st === 'ready') {
          readyAt = now
          // Something to find at once: the ISS, its pass drawn across the sky.
          if (layer.selected === null) layer.selectNorad(ISS_NORAD)
        }
      }
      lines.group.visible = st === 'ready'
      if (st === 'ready') updateGuides(camera)
      else pointer.update(camera, null, '')
      prompt.update(camera, promptText(st))
      layer.setSkyStatus(statusText())
    },

    onRayTap(origin, direction, camera) {
      if (!layer.skyOn) return false
      const st = stage()
      if (st === 'north') {
        northFromCamera(camera, 0)
        return true
      }
      if (st !== 'ready') return false
      const i = sats.pick(origin, direction, opts.handheld)
      if (i === null) return false
      layer.select(layer.selected === i ? null : i)
      return true
    },

    onGlobePlace(latDeg, lonDeg) {
      if (!layer.skyOn || stage() !== 'locate') return false
      setObserver({ latDeg, lonDeg })
      return true
    },

    resetNorth() {
      northYaw = null
      compass = null
    },


    dispose() {
      if (opts.handheld && typeof window !== 'undefined') {
        window.removeEventListener('deviceorientationabsolute', onOrientation as EventListener)
      }
      sats.dispose()
      prompt.dispose()
      pointer.dispose()
      lines.dispose()
      scene.remove(prompt.mesh)
      scene.remove(pointer.group)
      for (const s of letters) {
        s.material.map?.dispose()
        s.material.dispose()
      }
      guides.traverse((o) => {
        const line = o as THREE.Line
        if (line.isLine) line.geometry.dispose()
      })
      ringMat.dispose()
      faintMat.dispose()
      scene.remove(root)
    },
  }
}

function textSprite(THREE_: typeof THREE, text: string, colour: string): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 128
  canvas.height = 128
  const g = canvas.getContext('2d')!
  g.font = '700 84px system-ui, sans-serif'
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.lineWidth = 10
  g.strokeStyle = 'rgba(5, 8, 14, 0.85)'
  g.strokeText(text, 64, 68)
  g.fillStyle = colour
  g.fillText(text, 64, 68)
  const tex = new THREE_.CanvasTexture(canvas)
  tex.colorSpace = THREE_.SRGBColorSpace
  const sprite = new THREE_.Sprite(new THREE_.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false }))
  sprite.renderOrder = 11
  return sprite
}
